import { createContext, useCallback, useContext, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { Models } from 'appwrite';
import { account } from '../Services/appWriteConfig';
import { CreatePortalToken } from '../Services/PortalAuthServices';
import {
   clearPortalToken,
   clearSelectedCompany,
   getSelectedCompany,
   migrateStoredCredentials,
   rememberEmail,
   saveSelectedCompany,
} from './authStorage';

type Credentials = { email: string; password: string; companyId: string };
type AuthContextValue = {
   user: Models.User<Models.Preferences> | null;
   companyId: string | null;
   apiToken: string | null;
   rememberedEmail: string;
   handleUserLogin: (event: FormEvent<HTMLFormElement>, credentials: Credentials, remember: boolean) => Promise<void>;
   handleUserLogout: () => Promise<void>;
   handleSessionExpired: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function getAuthenticatedSession() {
   const user = await account.get();
   const jwt = await account.createJWT();
   const apiToken = await CreatePortalToken(jwt.jwt, user.$id);

   return { user, apiToken };
}

const dialogClasses = {
   popup: 'bg-slate-900 text-white rounded-2xl border border-slate-700',
   confirmButton: 'bg-cyan-400 hover:bg-cyan-300 text-slate-950 px-5 py-2 rounded-lg font-semibold',
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
   const [loading, setLoading] = useState(true);
   const [user, setUser] = useState<Models.User<Models.Preferences> | null>(null);
   const [companyId, setCompanyId] = useState<string | null>(null);
   const [apiToken, setApiToken] = useState<string | null>(null);
   const [rememberedEmail, setRememberedEmail] = useState('');
   const navigate = useNavigate();

   useEffect(() => {
      setRememberedEmail(migrateStoredCredentials());

      const restoreSession = async () => {
         try {
            const session = await getAuthenticatedSession();
            const selectedCompanyId = getSelectedCompany(session.user.$id);
            if (!selectedCompanyId) {
               await account.deleteSession('current').catch(() => undefined);
               setUser(null);
               setCompanyId(null);
               setApiToken(null);
               return;
            }

            setUser(session.user);
            setCompanyId(selectedCompanyId);
            setApiToken(session.apiToken);
         } catch {
            clearPortalToken();
            setUser(null);
            setCompanyId(null);
            setApiToken(null);
            // An expired Appwrite session must not prevent a later password sign-in.
            await account.deleteSession('current').catch(() => undefined);
         } finally {
            setLoading(false);
         }
      };

      void restoreSession();
   }, []);

   const handleUserLogin = useCallback(async (
      event: FormEvent<HTMLFormElement>,
      credentials: Credentials,
      shouldRemember: boolean,
   ) => {
      event.preventDefault();
      setLoading(true);

      Swal.fire({
         title: 'Giriş yapılıyor',
         text: 'Hesap bilgileriniz doğrulanıyor…',
         allowEscapeKey: false,
         allowOutsideClick: false,
         showConfirmButton: false,
         customClass: dialogClasses,
         didOpen: () => Swal.showLoading(),
      });

      try {
         try {
            await account.createEmailPasswordSession(credentials.email.trim(), credentials.password);
         } catch (error) {
            // Replace an earlier browser session only after Appwrite rejects a new session.
            if ((error as { type?: string }).type !== 'user_session_already_exists') throw error;
            await account.deleteSession('current');
            await account.createEmailPasswordSession(credentials.email.trim(), credentials.password);
         }

         if (!credentials.companyId) throw new Error('Lütfen bir firma seçin.');
         const session = await getAuthenticatedSession();

         saveSelectedCompany(session.user.$id, credentials.companyId);
         setUser(session.user);
         setCompanyId(credentials.companyId);
         setApiToken(session.apiToken);
         rememberEmail(credentials.email.trim(), shouldRemember);
         setRememberedEmail(shouldRemember ? credentials.email.trim() : '');

         await Swal.fire({
            icon: 'success',
            title: 'Giriş başarılı',
            text: 'Ana sayfaya yönlendiriliyorsunuz.',
            timer: 1200,
            showConfirmButton: false,
            customClass: dialogClasses,
         });
      } catch (error) {
         clearPortalToken();
         setUser(null);
         setCompanyId(null);
         setApiToken(null);
         await account.deleteSession('current').catch(() => undefined);

         const authError = error as { code?: number; message?: string };
         const message = authError.code === 401
            ? 'Kullanıcı adı veya şifre hatalı. Bilgilerinizi kontrol edip tekrar deneyin.'
            : authError.code === 429
               ? 'Çok fazla giriş denemesi yapıldı. Birkaç dakika sonra tekrar deneyin.'
               : authError.message || 'Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.';

         await Swal.fire({
            icon: 'error',
            title: 'Giriş yapılamadı',
            text: message,
            confirmButtonText: 'Tamam',
            customClass: dialogClasses,
         });
      } finally {
         setLoading(false);
      }
   }, []);

   const handleUserLogout = useCallback(async () => {
      try {
         await account.deleteSession('current');
      } finally {
         if (user) clearSelectedCompany(user.$id);
         clearPortalToken();
         setApiToken(null);
         setCompanyId(null);
         setUser(null);
         navigate('/', { replace: true });
      }
   }, [navigate, user]);

   const handleSessionExpired = useCallback(async () => {
      if (user) clearSelectedCompany(user.$id);
      clearPortalToken();
      setApiToken(null);
      setCompanyId(null);
      setUser(null);
      navigate('/', { replace: true });
      await account.deleteSession('current').catch(() => undefined);
   }, [navigate, user]);

   const value: AuthContextValue = {
      user,
      companyId,
      apiToken,
      rememberedEmail,
      handleUserLogin,
      handleUserLogout,
      handleSessionExpired,
   };

   return (
      <AuthContext.Provider value={value}>
         {loading ? (
            <main className="flex min-h-[calc(100vh-2.5rem)] items-center justify-center bg-slate-950 text-white">
               <div role="status" className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-cyan-400" />
                  Oturumunuz kontrol ediliyor…
               </div>
            </main>
         ) : children}
      </AuthContext.Provider>
   );
};

export function useAuth(): AuthContextValue {
   const context = useContext(AuthContext);
   if (!context) throw new Error('useAuth, AuthProvider içinde kullanılmalıdır.');
   return context;
}

export default AuthContext;
